import React, { useContext, useEffect, useState } from "react";
import Numeric from "./Numeric";
import { Row, Col, Button, Label } from "reactstrap";
import { Field } from "./Field";
import Combobox from "./Combobox";
import {
  OrderPriceType,
  getOrderTypeName,
  ProductType,
  getBaseAndPriceCurrencies,
} from "../../../../utils/Order/Order";
import { Dispatch as Context } from "../store/store";

const PriceType = (props) => {
  const { type, onChange, errors, readonly, form, maxDecimals, newSymbol } = props;
  const { state } = useContext(Context);
  const { entities, order } = state;
  const { settlementCurrencies } = entities;
  const { OrderCurrencySymbol, OrderSettlementCurrencySymbol } = order;

  const items = Object.values(OrderPriceType).reduce((obj, key) => {
    obj[key] = { id: key, description: getOrderTypeName(key) };
    return obj;
  }, {});

  const { baseCurrency, priceCurrency } = getBaseAndPriceCurrencies(
    settlementCurrencies,
    OrderCurrencySymbol,
    OrderSettlementCurrencySymbol
  );

  const typeItems = {
    [ProductType.BOND]: [items[OrderPriceType.MARKET], items[OrderPriceType.LIMIT]],

    [ProductType.EQUITY]: [
      items[OrderPriceType.MARKETONOPEN],
      items[OrderPriceType.MARKETONCLOSE],
      items[OrderPriceType.MARKET],
      items[OrderPriceType.LIMIT],
      items[OrderPriceType.STOP],
      items[OrderPriceType.STOPLIMIT],
    ],

    [ProductType.OPTION]: [
      items[OrderPriceType.MARKETONOPEN],
      items[OrderPriceType.MARKET],
      items[OrderPriceType.LIMIT],
      items[OrderPriceType.STOP],
      items[OrderPriceType.STOPLIMIT],
    ],

    [ProductType.MFUNDS]: [items[OrderPriceType.MARKET]],

    [ProductType.MMARKET]: [items[OrderPriceType.MARKET], items[OrderPriceType.LIMIT]],

    [ProductType.FOREX]: [items[OrderPriceType.MARKET], items[OrderPriceType.LIMIT]],

    [ProductType.REPO]: [items[OrderPriceType.MARKET], items[OrderPriceType.LIMIT]],

    [ProductType.FUTURE]: [items[OrderPriceType.MARKET], items[OrderPriceType.LIMIT]],
  };

  return (
    <div>
      <Row>
        <Col lg={6}>
          <Field label={"Type"} error={errors.type}>
            <Combobox
              value={type}
              items={typeItems[form]}
              disabled={readonly.type}
              onChange={(v) => onChange({ OrderType: v })}
              placeholder={"Type"}
              style={{ background: "#edf9ff", fontWeight: 500 }}
            />
          </Field>
        </Col>
        <Col lg={6}>
          <PriceField
            {...props}
            baseCurrency={baseCurrency}
            priceCurrency={priceCurrency}
            maxDecimals={maxDecimals}
            newSymbol={newSymbol}
          />
        </Col>
      </Row>
    </div>
  );
};

const PriceField = (props, maxDecimals, newSymbol) => {
  switch (props.type) {
    case OrderPriceType.MARKET:
      return null;
    case OrderPriceType.MARKETONOPEN:
      return null;
    case OrderPriceType.MARKETONCLOSE:
      return null;
    case OrderPriceType.LIMIT:
      return Single(props, maxDecimals, newSymbol);
    case OrderPriceType.STOP:
      return Single(props, maxDecimals, newSymbol);
    case OrderPriceType.STOPLIMIT:
      return StopLimit(props, maxDecimals, newSymbol);
    default:
      return null;
  }
};

const Single = ({
  type,
  form,
  label,
  values,
  oldValues,
  onChange,
  readonly,
  errors,
  currency,
  baseCurrency,
  priceCurrency,
  maxDecimals,
  newSymbol,
  editable,
}) => {
  const typePrice =
    type == OrderPriceType.LIMIT ||
    type == OrderPriceType.MARKETONOPEN ||
    type == OrderPriceType.MARKETONCLOSE
      ? "limPrice"
      : "stopPrice";
  const _label =
    label ||
    ([ProductType.MMARKET, ProductType.FOREX, ProductType.REPO].indexOf(form) >= 0 ? "Rate" : "Price");

  let prefix = "";
  // if (form !== ProductType.FOREX) {
  //   prefix = currency && currency !== "" ? ${currency}  : "";
  // } else {
  //   prefix = ${baseCurrency}/${priceCurrency} ;
  // }

  const [isEditing, setIsEditing] = useState(false);

  const handleEdit = () => {
    setIsEditing(true);
  };

  let suffix = "";
  if (form == ProductType.REPO) {
    suffix = "%";
  }

  let dataTypeDecimalName = "default";

  if (_label) {
    _label.toLowerCase() === "price" || "currency" || "percentage" || "quantity"
      ? (dataTypeDecimalName = _label.toLowerCase())
      : null;
  }

  useEffect(() => {
    values[typePrice] = Number(parseFloat(values[typePrice]).toFixed(maxDecimals));
    oldValues[typePrice] = Number(parseFloat(oldValues[typePrice]).toFixed(maxDecimals));
  }, [maxDecimals, newSymbol]);

  maxDecimals === undefined ? (maxDecimals = 8) : null;

  return (
    <Field
      label={_label}
      error={errors[typePrice]}
      showEdit={editable[typePrice] && !isEditing}
      onEditClick={handleEdit}
    >
      <Numeric
        value={values[typePrice]}
        placeholder={_label}
        disabled={readonly[typePrice] && !isEditing}
        decimalScale={maxDecimals}
        invalid={!!errors[typePrice]}
        prefix={prefix}
        suffix={suffix}
        dataTypeDecimalName={dataTypeDecimalName}
        onChange={(v) =>
          onChange({
            [type == OrderPriceType.LIMIT ||
            type == OrderPriceType.MARKETONOPEN ||
            type == OrderPriceType.MARKETONCLOSE
              ? "OrderPrice"
              : "OrderStopPrice"]: v,
          })
        }
      />
      {!Number.isNaN(oldValues[typePrice]) &&
        oldValues[typePrice] !== undefined &&
        oldValues[typePrice] != values[typePrice] && (
          <Label style={{ color: "red", fontSize: "12px" }}>Previous value: {oldValues[typePrice]}</Label>
        )}
    </Field>
  );
};

const StopLimit = ({
  values,
  oldValues,
  onChange,
  readonly,
  errors,
  currency,
  baseCurrency,
  priceCurrency,
  maxDecimals,
  newSymbol,
  editable,
}) => {
  const props = {
    onChange,
    values,
    oldValues,
    errors,
    readonly,
    currency,
    baseCurrency,
    priceCurrency,
    maxDecimals,
    newSymbol,
    editable,
  };

  return (
    <Row>
      <Col xs={12}>
        <Row>
          <Col xs={12}>
            <Single type={OrderPriceType.LIMIT} label={"Lim Price"} {...props} />
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <Single type={OrderPriceType.STOP} label={"Stop Price"} {...props} />
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default PriceType;
